/**
 * Freeze flow (#14 Phase C) — the ONLY entry point to create a Frozen Artwork,
 * available exclusively for a saved PIXEL_ART / Pixel Studio element.
 *
 * Sections: crop selection → integer placement → metadata + royalty → CC
 * allocation (auto proposal + manual override) → authoritative preview → strong
 * irreversible confirmation → real Freeze commit (idempotent).
 *
 * Every authoritative value (footprint, CC, placement validity, blocking errors,
 * proposed allocation, hash) comes from the backend preview — the preview call
 * performs ZERO mutation.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  artworkApi,
  ArtworkApiError,
  artworkCodeLabel,
  type FreezePreview,
  type AllocationItem,
} from "../../lib/artwork-api";

export interface FreezeParcel {
  id: number;
  x: number;
  y: number;
  w: number;
  h: number;
  tier: string;
}

export interface FreezeModalProps {
  identityId: string;
  elementId: string;
  sourceW: number;
  sourceH: number;
  parcels: FreezeParcel[];
  onClose: () => void;
  onFrozen: (artworkId: string) => void;
}

function clampInt(v: number, lo: number, hi: number): number {
  if (!Number.isFinite(v)) return lo;
  return Math.max(lo, Math.min(hi, Math.round(v)));
}

const GRID_SIZE = 1000;

export function FreezeModal({ identityId, elementId, sourceW, sourceH, parcels, onClose, onFrozen }: FreezeModalProps) {
  // Crop
  const [cropMode, setCropMode] = useState<"full" | "crop">("full");
  const [sx, setSx] = useState(0);
  const [sy, setSy] = useState(0);
  const [cw, setCw] = useState(sourceW);
  const [ch, setCh] = useState(sourceH);

  const effSx = cropMode === "full" ? 0 : clampInt(sx, 0, Math.max(0, sourceW - 1));
  const effSy = cropMode === "full" ? 0 : clampInt(sy, 0, Math.max(0, sourceH - 1));
  const effCw = cropMode === "full" ? sourceW : clampInt(cw, 1, sourceW - effSx);
  const effCh = cropMode === "full" ? sourceH : clampInt(ch, 1, sourceH - effSy);

  // Placement — default to a parcel that fits the crop, else the first parcel.
  const defaultParcel = useMemo(() => {
    return parcels.find((p) => p.w >= effCw && p.h >= effCh) ?? parcels[0] ?? null;
  }, [parcels, effCw, effCh]);
  const [anchorX, setAnchorX] = useState<number>(defaultParcel?.x ?? 0);
  const [anchorY, setAnchorY] = useState<number>(defaultParcel?.y ?? 0);

  // Metadata
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [royalty, setRoyalty] = useState<3 | 4 | 5>(3);

  // Allocation
  const [allocMode, setAllocMode] = useState<"auto" | "manual">("auto");
  const [manualAlloc, setManualAlloc] = useState<AllocationItem[]>([]);

  // Preview / commit state
  const [preview, setPreview] = useState<FreezePreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [commitError, setCommitError] = useState<string | null>(null);

  // A single idempotency key per modal session — reused for retries so a
  // double-click / retry never mints twice.
  const idemKey = useRef<string>(
    typeof crypto !== "undefined" && "randomUUID" in crypto ? crypto.randomUUID() : `idem-${Date.now()}-${Math.random()}`,
  );

  const allocationForRequest = allocMode === "manual" && manualAlloc.length > 0 ? manualAlloc : undefined;

  // Seed manual allocation from the auto proposal when switching to manual.
  useEffect(() => {
    if (allocMode === "manual" && manualAlloc.length === 0 && preview?.proposed_allocation) {
      setManualAlloc(preview.proposed_allocation.map((a) => ({ ...a })));
    }
  }, [allocMode, manualAlloc.length, preview]);

  const runPreview = useCallback(async (signal: AbortSignal) => {
    setPreviewLoading(true);
    setPreviewError(null);
    try {
      const p = await artworkApi.previewFreeze(
        {
          identity_id: identityId,
          element_id: elementId,
          source_x: effSx,
          source_y: effSy,
          width: effCw,
          height: effCh,
          anchor_x: anchorX,
          anchor_y: anchorY,
          allocation: allocationForRequest,
        },
        signal,
      );
      if (!signal.aborted) setPreview(p);
    } catch (e) {
      if (signal.aborted) return;
      setPreview(null);
      setPreviewError(e instanceof ArtworkApiError ? artworkCodeLabel(e.code) : "Preview failed");
    } finally {
      if (!signal.aborted) setPreviewLoading(false);
    }
  }, [identityId, elementId, effSx, effSy, effCw, effCh, anchorX, anchorY, allocationForRequest]);

  // Debounced authoritative preview whenever inputs change.
  useEffect(() => {
    const controller = new AbortController();
    const t = setTimeout(() => void runPreview(controller.signal), 250);
    return () => {
      clearTimeout(t);
      controller.abort();
    };
  }, [runPreview]);

  const manualTotal = manualAlloc.reduce((s, a) => s + (Number.isFinite(a.amount) ? a.amount : 0), 0);
  const titleValid = title.trim().length > 0 && title.trim().length <= 120;
  const allocOk =
    allocMode === "auto" ? preview?.proposed_allocation != null : manualTotal === (preview?.cc_required ?? -1);

  const canSubmit =
    !!preview &&
    preview.allowed_status &&
    preview.footprint >= preview.min_footprint &&
    preview.sufficient_cc &&
    preview.placement_valid &&
    allocOk &&
    titleValid &&
    confirmed &&
    !committing &&
    !previewLoading;

  async function doFreeze() {
    if (!preview) return;
    setCommitting(true);
    setCommitError(null);
    try {
      const art = await artworkApi.commitFreeze({
        identity_id: identityId,
        element_id: elementId,
        source_x: effSx,
        source_y: effSy,
        width: effCw,
        height: effCh,
        anchor_x: anchorX,
        anchor_y: anchorY,
        royalty_percent: royalty,
        title: title.trim(),
        description: description.trim() || undefined,
        allocation: allocationForRequest,
        idempotency_key: idemKey.current,
      });
      onFrozen(art.artwork_id);
    } catch (e) {
      const msg = e instanceof ArtworkApiError ? artworkCodeLabel(e.code) : "Freeze failed — please try again.";
      setCommitError(msg);
      // Re-run preview so any authoritative revalidation change is shown honestly.
      const c = new AbortController();
      void runPreview(c.signal);
    } finally {
      setCommitting(false);
    }
  }

  const nonBlockingWarnings = (preview?.warnings ?? []).filter(
    (w) => w !== "ARTWORK_PLACEMENT_OVERLAP" && w !== "ARTWORK_PLACEMENT_NOT_OWNED" && w !== "ARTWORK_PLACEMENT_OUT_OF_BOUNDS",
  );

  return (
    <div
      data-testid="freeze-modal"
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/70 p-4 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === e.currentTarget && !committing) onClose();
      }}
    >
      <div className="my-6 w-full max-w-2xl rounded-2xl border border-white/10 bg-[#0a0d14] p-6 shadow-2xl">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-mono text-sm uppercase tracking-[0.18em] text-white">Freeze Artwork</h2>
          <button data-testid="freeze-close" onClick={onClose} disabled={committing} className="text-white/50 hover:text-white disabled:opacity-40">✕</button>
        </div>

        {/* Authority gate */}
        {preview && !preview.allowed_status && (
          <div data-testid="freeze-not-allowed" className="mb-4 rounded-lg border border-amber-300/30 bg-amber-400/10 p-3 text-sm text-amber-200">
            Only Artists and NeoArtists can freeze artworks. Your current status is {preview.effective_status}.
          </div>
        )}

        {/* Crop */}
        <section className="mb-4">
          <h3 className="mb-2 font-mono text-[10px] uppercase tracking-[0.16em] text-white/50">Selection</h3>
          <div className="flex gap-2">
            <button data-testid="freeze-crop-full" onClick={() => setCropMode("full")} className={`rounded-full px-3 py-1.5 font-mono text-[11px] uppercase ${cropMode === "full" ? "bg-white/10 text-white" : "text-white/50"}`}>Full grid ({sourceW}×{sourceH})</button>
            <button data-testid="freeze-crop-rect" onClick={() => setCropMode("crop")} className={`rounded-full px-3 py-1.5 font-mono text-[11px] uppercase ${cropMode === "crop" ? "bg-white/10 text-white" : "text-white/50"}`}>Rectangular crop</button>
          </div>
          {cropMode === "crop" && (
            <div className="mt-3 grid grid-cols-4 gap-2">
              <IntField label="X" testid="freeze-sx" value={effSx} onChange={setSx} min={0} max={sourceW - 1} />
              <IntField label="Y" testid="freeze-sy" value={effSy} onChange={setSy} min={0} max={sourceH - 1} />
              <IntField label="W" testid="freeze-cw" value={effCw} onChange={setCw} min={1} max={sourceW - effSx} />
              <IntField label="H" testid="freeze-ch" value={effCh} onChange={setCh} min={1} max={sourceH - effSy} />
            </div>
          )}
        </section>

        {/* Placement */}
        <section className="mb-4">
          <h3 className="mb-2 font-mono text-[10px] uppercase tracking-[0.16em] text-white/50">Placement (integer anchor)</h3>
          <div className="grid grid-cols-2 gap-2">
            <IntField label="Anchor X" testid="freeze-anchor-x" value={anchorX} onChange={(v) => setAnchorX(clampInt(v, 0, GRID_SIZE - 1))} min={0} max={GRID_SIZE - 1} />
            <IntField label="Anchor Y" testid="freeze-anchor-y" value={anchorY} onChange={(v) => setAnchorY(clampInt(v, 0, GRID_SIZE - 1))} min={0} max={GRID_SIZE - 1} />
          </div>
          {parcels.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {parcels.map((p) => (
                <button
                  key={p.id}
                  data-testid={`freeze-parcel-${p.id}`}
                  onClick={() => { setAnchorX(p.x); setAnchorY(p.y); }}
                  className="rounded border border-white/10 px-2 py-1 font-mono text-[10px] text-white/60 hover:bg-white/5"
                >
                  #{p.id} ({p.x},{p.y})
                </button>
              ))}
            </div>
          )}
          <p className="mt-1 font-mono text-[10px] text-white/40">Rotation 0° · move &amp; rotation are out of #14 scope.</p>
        </section>

        {/* Metadata */}
        <section className="mb-4 grid grid-cols-1 gap-3">
          <label className="block">
            <span className="mb-1 block font-mono text-[10px] uppercase text-white/50">Title (immutable) *</span>
            <input data-testid="freeze-title" value={title} maxLength={120} onChange={(e) => setTitle(e.target.value)} className="w-full rounded-lg border border-white/10 bg-white/[0.02] px-3 py-2 text-sm text-white outline-none focus:border-teal-300/40" />
          </label>
          <label className="block">
            <span className="mb-1 block font-mono text-[10px] uppercase text-white/50">Description (immutable)</span>
            <textarea data-testid="freeze-description" value={description} maxLength={1000} rows={2} onChange={(e) => setDescription(e.target.value)} className="w-full rounded-lg border border-white/10 bg-white/[0.02] px-3 py-2 text-sm text-white outline-none focus:border-teal-300/40" />
          </label>
          <div>
            <span className="mb-1 block font-mono text-[10px] uppercase text-white/50">Royalty (immutable)</span>
            <div className="flex gap-2">
              {[3, 4, 5].map((r) => (
                <button key={r} data-testid={`freeze-royalty-${r}`} onClick={() => setRoyalty(r as 3 | 4 | 5)} className={`rounded-full px-4 py-1.5 font-mono text-[11px] ${royalty === r ? "bg-teal-400/20 text-teal-200 border border-teal-300/40" : "border border-white/10 text-white/50"}`}>{r}%</button>
              ))}
            </div>
          </div>
        </section>

        {/* Allocation */}
        <section className="mb-4">
          <div className="mb-2 flex items-center justify-between">
            <h3 className="font-mono text-[10px] uppercase tracking-[0.16em] text-white/50">Creative Capacity allocation</h3>
            <div className="flex gap-1">
              <button data-testid="freeze-alloc-auto" onClick={() => setAllocMode("auto")} className={`rounded-full px-3 py-1 font-mono text-[10px] uppercase ${allocMode === "auto" ? "bg-white/10 text-white" : "text-white/50"}`}>Auto</button>
              <button data-testid="freeze-alloc-manual" onClick={() => setAllocMode("manual")} className={`rounded-full px-3 py-1 font-mono text-[10px] uppercase ${allocMode === "manual" ? "bg-white/10 text-white" : "text-white/50"}`}>Manual</button>
            </div>
          </div>
          <div className="rounded-lg border border-white/10 bg-white/[0.02] p-3 text-sm">
            {allocMode === "auto" ? (
              (preview?.proposed_allocation ?? []).length === 0 ? (
                <div data-testid="freeze-alloc-none" className="text-white/40">No automatic allocation available.</div>
              ) : (
                <ul data-testid="freeze-alloc-proposed" className="space-y-1">
                  {preview!.proposed_allocation!.map((a) => (
                    <li key={a.source_id} className="flex justify-between font-mono text-[11px] text-white/70">
                      <span>Parcel #{a.source_id} · {a.tier}</span>
                      <span>{a.amount} CC</span>
                    </li>
                  ))}
                </ul>
              )
            ) : (
              <div data-testid="freeze-alloc-manual-editor" className="space-y-2">
                {manualAlloc.length === 0 && <div className="text-white/40">Switch produced no sources yet — adjust selection.</div>}
                {manualAlloc.map((a, i) => (
                  <div key={a.source_id} className="flex items-center justify-between gap-2 font-mono text-[11px] text-white/70">
                    <span>Parcel #{a.source_id} · {a.tier}</span>
                    <input
                      data-testid={`freeze-alloc-amount-${a.source_id}`}
                      type="number"
                      value={a.amount}
                      min={0}
                      onChange={(e) => {
                        const v = clampInt(Number(e.target.value), 0, 10_000_000);
                        setManualAlloc((prev) => prev.map((x, xi) => (xi === i ? { ...x, amount: v } : x)));
                      }}
                      className="w-24 rounded border border-white/10 bg-white/[0.02] px-2 py-1 text-right text-white"
                    />
                  </div>
                ))}
              </div>
            )}
            <div className="mt-2 flex justify-between border-t border-white/5 pt-2 font-mono text-[11px]">
              <span className="text-white/50">Selected / required</span>
              <span data-testid="freeze-alloc-totals" className={allocOk ? "text-teal-300" : "text-amber-300"}>
                {allocMode === "manual" ? manualTotal : (preview?.cc_required ?? 0)} / {preview?.cc_required ?? 0} CC
              </span>
            </div>
          </div>
        </section>

        {/* Authoritative preview summary */}
        <section className="mb-4 rounded-lg border border-white/10 bg-white/[0.02] p-3">
          <div className="grid grid-cols-2 gap-2 font-mono text-[11px] text-white/70 sm:grid-cols-4">
            <Stat label="Footprint" testid="freeze-footprint" value={`${preview?.footprint ?? "–"}`} />
            <Stat label="CC required" testid="freeze-cc-required" value={`${preview?.cc_required ?? "–"}`} />
            <Stat label="CC available" testid="freeze-cc-available" value={`${preview?.remaining_cc ?? "–"}`} />
            <Stat label="Placement" testid="freeze-placement-status" value={preview ? (preview.placement_valid ? "valid" : "invalid") : "–"} tint={preview?.placement_valid ? "text-teal-300" : "text-amber-300"} />
          </div>
          <div className="mt-2 font-mono text-[10px] text-white/40">
            Hash: <span data-testid="freeze-content-hash">{preview?.content_hash ?? "–"}</span>
          </div>
          {previewLoading && <div data-testid="freeze-preview-loading" className="mt-1 text-[10px] text-white/40">Revalidating…</div>}
          {previewError && <div data-testid="freeze-preview-error" className="mt-2 text-sm text-red-300">{previewError}</div>}
          {preview && preview.warnings.includes("ARTWORK_PLACEMENT_OVERLAP") && (
            <div data-testid="freeze-overlap" className="mt-2 text-sm text-amber-300">{artworkCodeLabel("ARTWORK_PLACEMENT_OVERLAP")}</div>
          )}
          {preview && preview.warnings.includes("ARTWORK_PLACEMENT_NOT_OWNED") && (
            <div data-testid="freeze-not-owned" className="mt-2 text-sm text-amber-300">{artworkCodeLabel("ARTWORK_PLACEMENT_NOT_OWNED")}</div>
          )}
          {preview && preview.warnings.includes("ARTWORK_PLACEMENT_OUT_OF_BOUNDS") && (
            <div data-testid="freeze-oob" className="mt-2 text-sm text-amber-300">{artworkCodeLabel("ARTWORK_PLACEMENT_OUT_OF_BOUNDS")}</div>
          )}
          {preview && !preview.sufficient_cc && (
            <div data-testid="freeze-insufficient-cc" className="mt-2 text-sm text-amber-300">{artworkCodeLabel("ARTWORK_INSUFFICIENT_CC")}</div>
          )}
          {preview && preview.footprint < preview.min_footprint && (
            <div data-testid="freeze-too-small" className="mt-2 text-sm text-amber-300">{artworkCodeLabel("ARTWORK_FOOTPRINT_TOO_SMALL")}</div>
          )}
          {nonBlockingWarnings.map((w) => (
            <div key={w} className="mt-1 text-[10px] text-white/40">{artworkCodeLabel(w)}</div>
          ))}
        </section>

        {/* Irreversible confirmation */}
        <label className="mb-3 flex items-start gap-2 rounded-lg border border-red-300/20 bg-red-400/5 p-3 text-sm text-white/80">
          <input data-testid="freeze-confirm" type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} className="mt-0.5" />
          <span><strong className="text-red-300">AFTER FREEZE THIS ARTWORK CANNOT BE EDITED</strong>, resized, moved-in-content or deleted. The frozen content and metadata are permanent.</span>
        </label>

        {commitError && <div data-testid="freeze-commit-error" className="mb-3 rounded-lg border border-red-300/30 bg-red-400/10 p-2 text-sm text-red-300">{commitError}</div>}

        <div className="flex items-center justify-end gap-3">
          <button data-testid="freeze-cancel" onClick={onClose} disabled={committing} className="rounded-full border border-white/15 px-4 py-2 font-mono text-[11px] uppercase tracking-[0.16em] text-white/60 disabled:opacity-40">Cancel</button>
          <button
            data-testid="freeze-submit"
            onClick={() => void doFreeze()}
            disabled={!canSubmit}
            className="rounded-full border border-red-300/40 bg-red-400/15 px-5 py-2 font-mono text-[11px] uppercase tracking-[0.16em] text-red-200 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {committing ? "Freezing…" : "Freeze permanently"}
          </button>
        </div>
      </div>
    </div>
  );
}

function IntField({ label, value, onChange, min, max, testid }: { label: string; value: number; onChange: (v: number) => void; min: number; max: number; testid: string }) {
  return (
    <label className="block">
      <span className="mb-1 block font-mono text-[9px] uppercase text-white/40">{label}</span>
      <input
        data-testid={testid}
        type="number"
        value={value}
        min={min}
        max={max}
        step={1}
        onChange={(e) => onChange(Math.round(Number(e.target.value)))}
        className="w-full rounded border border-white/10 bg-white/[0.02] px-2 py-1.5 text-sm text-white outline-none focus:border-teal-300/40"
      />
    </label>
  );
}

function Stat({ label, value, testid, tint }: { label: string; value: string; testid: string; tint?: string }) {
  return (
    <div>
      <div className="text-[9px] uppercase text-white/40">{label}</div>
      <div data-testid={testid} className={tint ?? "text-white/80"}>{value}</div>
    </div>
  );
}
